import { eckoDispatch } from '../utils/analytics';
import { getCookie, setCookie } from '../utils/cookies';
import { showToast } from './toast';


const DISMISS_ICON = '<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="icon icon-tabler icons-tabler-outline icon-tabler-circle-minus"><path stroke="none" d="M0 0h24v24H0z" fill="none" /><path d="M3 12a9 9 0 1 0 18 0a9 9 0 1 0 -18 0" /><path d="M9 12l6 0" /></svg>';
const UNDO_DISMISS_ICON = '<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="icon icon-tabler icons-tabler-outline icon-tabler-circle-plus"><path stroke="none" d="M0 0h24v24H0z" fill="none" /><path d="M3 12a9 9 0 1 0 18 0a9 9 0 0 0 -18 0" /><path d="M9 12h6" /><path d="M12 9v6" /></svg>';

function getDismissButtonHtml(isDismissed) {
	return isDismissed ? `Undo Dismiss ${UNDO_DISMISS_ICON}` : `Dismiss ${DISMISS_ICON}`;
}

export function initNewsletterReader() {
	const list = document.querySelector('.yeargroup-list');
	const itemInputs = list ? Array.from(list.querySelectorAll('input.yeargroup-toggle')) : [];
	const allCheckbox = list ? list.querySelector('#yeargroup-all') : null;
	const groupFilterInputs = itemInputs.filter(input => input !== allCheckbox);
	const groupFilterCountEl = document.getElementById('yeargroup-filter-count');
	const articles = Array.from(document.querySelectorAll('.newsletter-story-block'));
	const tocItems = Array.from(document.querySelectorAll('.table-of-contents .toc-list li'));
	const tocMap = tocItems
		.map(item => {
			const sel = item.getAttribute('data-target');
			if (!sel) return null;
			const target = document.querySelector(sel);
			if (!target) return null;
			return { item, target };
		})
		.filter(Boolean);

	let activeAnchorArticle = null;
	let activeAnchorToastKey = null;

	function emitYeargroupAnalytics(reason) {
		const enabledSlugs = itemInputs
			.filter(i => i.checked)
			.map(i => i.value);

		eckoDispatch('ecko:yeargroupFilterChange', {
			reason,
			enabledSlugs
		});
	}

	function getEnabledSet() {
		return new Set(itemInputs.filter(i => i.checked).map(i => i.value));
	}

	function areAllFiltersChecked() {
		return itemInputs.length === 0 || itemInputs.every(cb => cb.checked);
	}

	function setAllFiltersChecked() {
		itemInputs.forEach(cb => {
			cb.checked = true;
		});
	}

	function updateGroupFilterCount() {
		if (!groupFilterCountEl) return;

		const activeCount = groupFilterInputs.filter(input => input.checked).length;
		groupFilterCountEl.textContent = `${activeCount}⁄${groupFilterInputs.length}`;
	}

	function updateFilterWarning() {
		const warning = document.getElementById('filter-warning');
		if (!warning) return;

		const anyChecked = itemInputs.length === 0 || itemInputs.some(i => i.checked);
		warning.setAttribute('aria-hidden', String(anyChecked));
		if (anyChecked) {
			warning.classList.add('hidden');
		} else {
			warning.classList.remove('hidden');
		}
	}

	function updateAllState() {
		if (allCheckbox) {
			allCheckbox.checked = areAllFiltersChecked();
		}
		updateGroupFilterCount();
		updateFilterWarning();
	}

	function savePreferences() {
		if (itemInputs.length === 0) return;

		const enabled = itemInputs.filter(i => i.checked).map(i => i.value);
		setCookie('newsletter_yeargroups', JSON.stringify(enabled), 365);
	}

	function restorePreferences() {
		if (itemInputs.length === 0) {
			updateAllState();
			return;
		}

		const raw = getCookie('newsletter_yeargroups');
		if (!raw) {
			setAllFiltersChecked();
			updateAllState();
			return;
		}

		try {
			const enabled = JSON.parse(raw);
			if (!Array.isArray(enabled)) throw new Error('invalid');
			const enabledSet = new Set(enabled.map(String));

			itemInputs.forEach(i => {
				i.checked = enabledSet.has(i.value);
			});
		} catch (e) {
			setAllFiltersChecked();
		}

		if (!itemInputs.some(i => i.checked)) {
			setAllFiltersChecked();
		}

		updateAllState();
	}

	function articleMatches(article, enabledSet) {
		if (itemInputs.length === 0) return true;

		const attr = article.getAttribute('data-yeargroups') || '';
		const groups = attr.split(/\s+/).filter(Boolean);
		if (groups.includes('all')) return true;
		return groups.some(g => enabledSet.has(g));
	}

	function tocItemMatches(item, enabledSet) {
		if (itemInputs.length === 0) return true;

		const attr = item.getAttribute('data-yeargroups') || '';
		const groups = attr.split(/\s+/).filter(Boolean);
		if (groups.includes('all')) return true;
		return groups.some(g => enabledSet.has(g));
	}

	function isActiveAnchorArticle(article) {
		return activeAnchorArticle === article;
	}

	function getTocItemForArticle(article) {
		if (!article?.id) return null;
		return tocItems.find(item => item.getAttribute('data-target') === `#${article.id}`) || null;
	}

	function showTocItem(item) {
		if (!item) return;

		item.classList.remove('hidden');
		item.setAttribute('aria-hidden', 'false');
	}

	function hideTocItem(item) {
		if (!item) return;

		const target = item.getAttribute('data-target');
		if (activeAnchorArticle && target === `#${activeAnchorArticle.id}`) {
			showTocItem(item);
			return;
		}

		item.classList.add('hidden');
		item.setAttribute('aria-hidden', 'true');
	}

	function showArticle(article) {
		if (!article) return;

		if (!article.classList.contains('is-filter-hidden')) {
			article.setAttribute('aria-hidden', 'false');
			return;
		}

		article.classList.remove('is-filter-hidden');
		article.classList.add('is-filter-showing');

		const target = article.scrollHeight;
		article.style.maxHeight = target + 24 + 'px';

		const onEnd = function (e) {
			if (e.propertyName !== 'max-height') return;
			article.style.maxHeight = '';
			article.classList.remove('is-filter-showing');
			article.setAttribute('aria-hidden', 'false');
			article.removeEventListener('transitionend', onEnd);
		};
		article.addEventListener('transitionend', onEnd);
	}

	function forceShowArticle(article) {
		if (!article) return;

		article.classList.remove('is-filter-hidden', 'is-filter-showing');
		article.style.maxHeight = '';
		article.setAttribute('aria-hidden', 'false');
	}

	function hideArticle(article) {
		if (!article) return;

		if (isActiveAnchorArticle(article)) {
			forceShowArticle(article);
			return;
		}

		if (article.classList.contains('is-filter-hidden')) return;

		const current = article.scrollHeight;
		article.style.maxHeight = current + 24 + 'px';
		article.getBoundingClientRect();

		article.classList.add('is-filter-hidden');
		article.setAttribute('aria-hidden', 'true');
		article.style.maxHeight = '0px';

		const onEnd = function (e) {
			if (e.propertyName !== 'max-height') return;
			article.setAttribute('aria-hidden', 'true');
			article.removeEventListener('transitionend', onEnd);
		};
		article.addEventListener('transitionend', onEnd);
	}

	function markAnchorArticle(article) {
		if (!article) return;

		article.classList.add('is-anchor-target', 'is-temporarily-visible');
		article.setAttribute('data-temporarily-visible', 'true');
		article.setAttribute('data-temporary-visibility-reason', 'anchor');
		forceShowArticle(article);
		showTocItem(getTocItemForArticle(article));
	}

	function unmarkAnchorArticle(article) {
		if (!article) return;

		article.classList.remove('is-anchor-target', 'is-temporarily-visible');
		article.removeAttribute('data-temporarily-visible');
		article.removeAttribute('data-temporary-visibility-reason');
	}

	function getArticleFromHash() {
		if (!window.location.hash || window.location.hash === '#') return null;

		let anchor = window.location.hash.slice(1);
		try {
			anchor = decodeURIComponent(anchor);
		} catch (error) {
			return null;
		}

		return articles.find(article => article.id === anchor) || null;
	}

	function isArticleHiddenByReaderState(article) {
		if (!article) return false;

		const id = article.getAttribute('data-story-id');
		const isDismissable = article.getAttribute('data-dismissable') === 'true';
		const hiddenByDismissal = isDismissable && DismissManager.isDismissed(id) && !DismissManager.showDismissed();
		const hiddenByGroupFilters = !articleMatches(article, getEnabledSet());

		return hiddenByDismissal || hiddenByGroupFilters;
	}

	function updateAnchorArticleFromHash() {
		const nextArticle = getArticleFromHash();
		const shouldShowTemporaryUnhideToast = nextArticle && isArticleHiddenByReaderState(nextArticle);

		if (activeAnchorArticle && activeAnchorArticle !== nextArticle) {
			unmarkAnchorArticle(activeAnchorArticle);
		}

		activeAnchorArticle = nextArticle;

		if (activeAnchorArticle) {
			markAnchorArticle(activeAnchorArticle);

			if (shouldShowTemporaryUnhideToast && activeAnchorToastKey !== activeAnchorArticle.id) {
				showToast({
					message: "You followed a link to a story that you've dismissed or hidden via your Group Filters. It's been temporarily un-hidden.",
					mode: 'info',
					icon: 'info',
					duration: 15000,
				});
				activeAnchorToastKey = activeAnchorArticle.id;
			} else if (!shouldShowTemporaryUnhideToast) {
				activeAnchorToastKey = null;
			}
		} else {
			activeAnchorToastKey = null;
		}
	}

	function applyAnchorOverride() {
		if (!activeAnchorArticle) return;

		markAnchorArticle(activeAnchorArticle);
	}

	function updateTocCount() {
		const countEl = document.getElementById('toc-count');
		if (!countEl) return;
		const visibleItems = tocItems.filter(item => item.getAttribute('aria-hidden') !== 'true');
		countEl.textContent = visibleItems.length;
	}

	function applyFilters() {
		const enabled = getEnabledSet();

		articles.forEach(article => {
			const id = article.getAttribute('data-story-id');
			const isDismissed = DismissManager.isDismissed(id);
			const isDismissable = article.getAttribute('data-dismissable') === 'true';

			if (isActiveAnchorArticle(article)) {
				forceShowArticle(article);
			} else if (isDismissed && isDismissable && !DismissManager.showDismissed()) {
				hideArticle(article);
			} else if (articleMatches(article, enabled)) {
				showArticle(article);
			} else {
				hideArticle(article);
			}
		});

		tocItems.forEach(item => {
			const href = item.querySelector('a')?.getAttribute('href');
			if (!href) return;

			const article = articles.find(candidate => `#${candidate.id}` === href);
			if (article && isActiveAnchorArticle(article)) {
				showTocItem(item);
			} else if (tocItemMatches(item, enabled) && (!article?.classList.contains('dismissed') || DismissManager.showDismissed())) {
				showTocItem(item);
			} else {
				hideTocItem(item);
			}
		});

		applyAnchorOverride();
		DismissManager.updateSummary();
		updateTocCount();
	}

	if (tocMap.length > 0) {
		const observer = new IntersectionObserver(
			entries => {
				entries.forEach(entry => {
					if (!entry.isIntersecting) return;
					if (entry.target.classList.contains('is-filter-hidden') || entry.target.getAttribute('aria-hidden') === 'true') return;

					const id = '#' + entry.target.id;

					tocItems.forEach(i => i.classList.remove('current'));

					const match = tocMap.find(x => '#' + x.target.id === id);
					if (match) {
						match.item.classList.add('current');
					}
				});
			},
			{
				root: null,
				rootMargin: '-10% 0px -60% 0px',
				threshold: 0
			}
		);

		tocMap.forEach(({ target }) => observer.observe(target));
	}

	const toTopButton = document.querySelector('#to-top');
	if (toTopButton) {
		toTopButton.addEventListener('click', event => {
			event.preventDefault();

			const topAnchor = document.querySelector('#top');
			if (topAnchor) {
				topAnchor.scrollIntoView({
					behavior: 'smooth'
				});
			}
		});
	}

	if (allCheckbox) {
		allCheckbox.addEventListener('change', function () {
			setAllFiltersChecked();

			updateAllState();
			savePreferences();
			applyFilters();
			emitYeargroupAnalytics('reset');
		});
	}

	itemInputs.forEach(i => {
		i.addEventListener('change', function () {
			const checkedCount = itemInputs.filter(input => input.checked).length;
			const allButClickedRemainChecked = !this.checked && checkedCount === itemInputs.length - 1;

			if (checkedCount === 0) {
				setAllFiltersChecked();
				updateAllState();
				savePreferences();
				applyFilters();
				emitYeargroupAnalytics('reset');
				return;
			}

			if (allButClickedRemainChecked) {
				itemInputs.forEach(other => {
					other.checked = other === this;
				});
			}

			updateAllState();
			savePreferences();
			applyFilters();
			emitYeargroupAnalytics('single-toggle');
		});
	});

	const DismissManager = (function () {
		const dismissedStoriesCookieName = 'newsletter_dismissed_stories';
		let dismissedIds = new Set();
		let showDismissed = false;

		const yeargroupSummaryItem = document.querySelector('.group-filter-hidden-count');
		const yeargroupCountEl = yeargroupSummaryItem?.querySelector('#hidden-count');
		const dismissalSummaryItem = document.querySelector('.dismissed-story-count');
		const dismissalCountEl = dismissalSummaryItem?.querySelector('#dismissed-count');

		function init() {
			loadDismissed();
			renderDismissedState();
			setupEventListeners();
			updateDismissedToggleState();
			updateSummary();
		}

		function loadDismissed() {
			try {
				const saved = getCookie(dismissedStoriesCookieName);
				if (saved) {
					const ids = JSON.parse(saved);
					if (Array.isArray(ids)) {
						dismissedIds = new Set(ids.map(String));
					}
				}
			} catch (e) {
				console.error('Error loading dismissed stories', e);
			}
		}

		function saveDismissed() {
			setCookie(dismissedStoriesCookieName, JSON.stringify(Array.from(dismissedIds)), 365);
		}

		function renderDismissedState(animate = false) {
			articles.forEach(article => {
				const id = article.getAttribute('data-story-id');
				const isDismissed = dismissedIds.has(id);
				const isDismissable = article.getAttribute('data-dismissable') === 'true';
				const btn = article.querySelector('.js-dismiss-story');

				if (isDismissed && isDismissable) {
					article.classList.add('dismissed');
					if (btn) {
						btn.innerHTML = getDismissButtonHtml(true);
						btn.setAttribute('aria-label', 'Undo dismiss this story');
					}

					if (showDismissed || isActiveAnchorArticle(article)) {
						showStory(article, animate);
					} else {
						hideStory(article, animate);
					}
				} else {
					article.classList.remove('dismissed');
					if (btn) {
						btn.innerHTML = getDismissButtonHtml(false);
						btn.setAttribute('aria-label', 'Dismiss this story');
					}

					const enabled = getEnabledSet();
					if (articleMatches(article, enabled) || isActiveAnchorArticle(article)) {
						showStory(article, animate);
					}
				}
			});

			updateTocCount();
		}

		function hideStory(article, animate = false) {
			if (isActiveAnchorArticle(article)) {
				showStory(article, false);
				return;
			}

			if (animate) {
				hideArticle(article);
			} else {
				article.classList.add('is-filter-hidden');
				article.style.maxHeight = '0px';
				article.setAttribute('aria-hidden', 'true');
			}

			hideTocItem(getTocItemForArticle(article));
		}

		function showStory(article, animate = false) {
			if (isActiveAnchorArticle(article)) {
				forceShowArticle(article);
			} else if (animate) {
				showArticle(article);
			} else {
				article.classList.remove('is-filter-hidden', 'is-filter-showing');
				article.style.maxHeight = '';
				article.setAttribute('aria-hidden', 'false');
			}

			showTocItem(getTocItemForArticle(article));
		}

		function setupEventListeners() {
			document.addEventListener('click', e => {
				const dismissBtn = e.target.closest('.js-dismiss-story');
				if (dismissBtn) {
					const storyId = dismissBtn.getAttribute('data-story-id');
					toggleDismissal(storyId);
					return;
				}

				const showDismissedLink = e.target.closest('#show-dismissed-link');
				if (showDismissedLink) {
					e.preventDefault();
					toggleShowDismissed();
					return;
				}
			});
		}

		function toggleDismissal(id) {
			if (dismissedIds.has(id)) {
				dismissedIds.delete(id);
				eckoDispatch('ecko:storyUndoDismiss', { storyId: id });
				showToast({
					message: 'Recurring story un-dismissed.',
					mode: 'success',
					icon: 'success'
				});
			} else {
				dismissedIds.add(id);
				eckoDispatch('ecko:storyDismiss', { storyId: id });
				showToast({
					message: 'Recurring story dismissed. You can show dismissed stories from the \'In This Edition\' panel.',
					mode: 'success',
					icon: 'success'
				});
			}

			saveDismissed();
			renderDismissedState(true);
			applyAnchorOverride();
			updateSummary();
		}

		function toggleShowDismissed() {
			showDismissed = !showDismissed;
			document.body.classList.toggle('show-dismissed', showDismissed);
			updateDismissedToggleState();

			renderDismissedState(true);
			applyFilters();

			if (showDismissed) {
				const firstDismissed = articles.find(a => a.classList.contains('dismissed'));
				if (firstDismissed) {
					setTimeout(() => {
						firstDismissed.scrollIntoView({ behavior: 'smooth', block: 'center' });
					}, 100);
				}
			}
		}

		function updateDismissedToggleState() {
			const link = document.getElementById('show-dismissed-link');
			if (!link) return;

			const label = showDismissed ? 'Hide dismissed stories' : 'Show dismissed stories';
			link.classList.toggle('dismissed-shown', showDismissed);
			link.classList.toggle('dismissed-hidden', !showDismissed);
			link.setAttribute('aria-pressed', String(showDismissed));
			link.setAttribute('aria-label', label);
			link.setAttribute('title', label);
		}

		function isArticleDismissed(article) {
			if (!article) return false;
			const id = article.getAttribute('data-story-id');
			const isDismissable = article.getAttribute('data-dismissable') === 'true';
			return isDismissable && dismissedIds.has(id);
		}

		function updateSummary() {
			const enabled = getEnabledSet();
			const hasGroupFiltersApplied = itemInputs.length > 0 && !areAllFiltersChecked();
			const hiddenByFilterCount = hasGroupFiltersApplied ? articles.filter(article => {
				return !isActiveAnchorArticle(article) && !isArticleDismissed(article) && !articleMatches(article, enabled);
			}).length : 0;

			if (yeargroupCountEl) yeargroupCountEl.textContent = hiddenByFilterCount;
			if (yeargroupSummaryItem) {
				yeargroupSummaryItem.hidden = !hasGroupFiltersApplied;
				yeargroupSummaryItem.classList.toggle('hidden', !hasGroupFiltersApplied);
				yeargroupSummaryItem.style.display = hasGroupFiltersApplied ? '' : 'none';
			}

			const dismissedCount = articles.filter(a => a.classList.contains('dismissed')).length;
			if (dismissalCountEl) dismissalCountEl.textContent = dismissedCount;
			if (dismissalSummaryItem) {
				const hasDismissedStories = dismissedCount > 0;
				dismissalSummaryItem.hidden = !hasDismissedStories;
				dismissalSummaryItem.classList.toggle('hidden', !hasDismissedStories);
				dismissalSummaryItem.style.display = hasDismissedStories ? '' : 'none';
			}
		}

		return {
			init,
			updateSummary,
			isDismissed: id => dismissedIds.has(id),
			showDismissed: () => showDismissed
		};
	})();

	restorePreferences();
	DismissManager.init();
	updateAnchorArticleFromHash();
	applyFilters();

	window.addEventListener('hashchange', () => {
		updateAnchorArticleFromHash();
		applyFilters();
	});

	(() => {
		const main = document.querySelector('#main-content');
		const bar = document.querySelector('#progressBar');
		if (!main || !bar) return;

		const clamp01 = n => (n < 0 ? 0 : n > 1 ? 1 : n);

		let vh = window.innerHeight || document.documentElement.clientHeight;
		let mainHeight = main.getBoundingClientRect().height || 1;
		let ticking = false;
		let lastWidth = '';

		const computeAndPaint = () => {
			ticking = false;

			const rect = main.getBoundingClientRect();
			const progress = clamp01((vh - rect.top) / Math.max(1, mainHeight));
			const width = `${(progress * 100).toFixed(2)}%`;

			if (width !== lastWidth) {
				bar.style.width = width;
				lastWidth = width;
			}
		};

		const requestTick = () => {
			if (ticking) return;
			ticking = true;
			requestAnimationFrame(computeAndPaint);
		};

		const onResize = () => {
			vh = window.innerHeight || document.documentElement.clientHeight;
			mainHeight = main.getBoundingClientRect().height || 1;
			requestTick();
		};

		window.addEventListener('scroll', requestTick, { passive: true });
		window.addEventListener('resize', onResize, { passive: true });

		if (window.ResizeObserver) {
			const ro = new ResizeObserver(() => {
				mainHeight = main.getBoundingClientRect().height || 1;
				requestTick();
			});
			ro.observe(main);
		}

		onResize();
	})();
}